/**
 * RemoveInformantFlow
 * ===================
 *
 * Single-step confirmation flow for permanently removing an informant.
 *
 * Unlike ReplaceInformantFlow (which collects new informant details in a
 * two-step wizard), this flow only needs the user to understand the
 * consequences and confirm.  The single screen shows:
 *
 *   - A tiered warning panel whose severity escalates with the informant's
 *     progress (not signed in < signed in < started < completed)
 *   - An informant summary card (name, email, relationship, life stages)
 *   - An optional reason text input for the audit trail
 *   - A destructive "Confirm removal" button
 *
 * On success, a ConfirmationScreen is shown.  The component owns all local
 * state internally (same isolation pattern as ReplaceInformantFlow), so
 * unmounting it resets cleanly and Firestore onSnapshot-triggered re-renders
 * in the parent cannot tear away the success screen.
 *
 * The backend `deactivateInformantEndpoint` archives the informant's data
 * to `deactivated_informants`, removes the `informants.{uid}` map entry,
 * and deletes the Firebase Auth account — making the action irreversible
 * from the user's perspective.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import WarningPanel from './WarningPanel';
import ConfirmationScreen from './ConfirmationScreen';
import ButtonSpinner from './ButtonSpinner';

/**
 * @param {Object}   props
 * @param {Object}   props.informant         - The informant being removed
 * @param {Function} props.onRemove          - (informantId, reason) => Promise
 * @param {Function} props.onBack            - Navigate back to the detail screen
 * @param {boolean}  props.actionLoading     - Whether the backend call is in progress
 * @param {string}   props.actionError       - Error message from the backend
 * @param {Function} props.clearActionState  - Reset actionError/actionSuccess
 * @param {Function} props.onFinish          - Called when user clicks "Finish" on success screen
 * @param {boolean}  props.isFinishing       - Show spinner on the "Finish" button
 * @param {Object}   props.terminology       - { singular, lowerSingular }
 * @param {string}   props.callerType        - 'patient' | 'provider'
 * @param {boolean}  props.isAutismPathway   - Whether this is the adult autism pathway
 */
export default function RemoveInformantFlow({
  informant,
  onRemove,
  onBack,
  actionLoading = false,
  actionError = null,
  clearActionState,
  onFinish,
  isFinishing = false,
  terminology = { singular: 'Informant', lowerSingular: 'informant' },
  callerType = 'provider',
  isAutismPathway = false,
}) {
  const [reason, setReason] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Guard against setting state after the component is unmounted (e.g. if
  // the parent navigates away before the async onRemove resolves).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── Determine warning tier ──
  // The tier escalates with the informant's progress: the more work they
  // have done, the more prominent the warning.  This matches the graduated
  // approach used by ReplaceInformantFlow (hadProgress → warning vs info).
  const hasProgress = informant?.hasStarted;
  const hasCompleted = informant?.hasCompleted;
  const hasSignedIn = informant?.hasSetOwnPassword;

  // Multi-stage ADHD informants get an extra contextual note because
  // removal affects both life-stage questionnaire assignments at once.
  const isMultiStage =
    !isAutismPathway && (informant?.lifeStages?.length === 2);

  const handleConfirm = useCallback(async () => {
    clearActionState?.();
    try {
      await onRemove(informant.id, reason.trim() || 'Manual removal');
      if (mountedRef.current) setIsSuccess(true);
    } catch {
      // actionError handled by the hook via callFunction
    }
  }, [onRemove, informant, reason, clearActionState]);

  // ── Success screen ──
  if (isSuccess) {
    return (
      <ConfirmationScreen
        headline={`${terminology.singular} removed successfully`}
        detail={`${informant.firstName} ${informant.lastName} has been removed and their access has been revoked.`}
        onFinish={onFinish}
        isLoading={isFinishing}
      />
    );
  }

  // ── Confirmation screen ──
  return (
    <div className="space-y-4">
      <div className="border-b border-gray-200 pb-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Remove {terminology.singular}
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          This will permanently remove{' '}
          <span className="font-medium">{informant.firstName} {informant.lastName}</span>{' '}
          and revoke their access.
        </p>
      </div>

      {/* Tiered warning — severity escalates with informant progress */}
      <WarningPanel variant={hasProgress || hasCompleted ? 'warning' : 'info'}>
        {hasCompleted ? (
          <>
            <p className="font-semibold mb-1">Important: This cannot be undone.</p>
            <p>
              This {terminology.lowerSingular} has completed their questionnaires.
              Removing them will revoke their access and their responses will not
              be included in the assessment.
            </p>
          </>
        ) : hasProgress ? (
          <>
            <p className="font-semibold mb-1">Important: This cannot be undone.</p>
            <p>
              This {terminology.lowerSingular} has started their questionnaires.
              Removing them will revoke their access and their responses will not
              be included in the assessment.
            </p>
          </>
        ) : hasSignedIn ? (
          <p>
            This {terminology.lowerSingular} has signed in but has not started any
            questionnaires. Removing them will revoke their access.
          </p>
        ) : (
          <p>
            This {terminology.lowerSingular} has not yet signed in. Removing them
            will revoke their access.
          </p>
        )}
      </WarningPanel>

      {/* Extra warning for ADHD informants covering both life stages */}
      {isMultiStage && (
        <WarningPanel variant="info">
          This {terminology.lowerSingular} is assigned to both adulthood and
          childhood questionnaires. Both assignments will be removed.
        </WarningPanel>
      )}

      {/* Informant summary card — lets the user verify they are removing
          the right person before confirming */}
      <div className="bg-gray-50 rounded-md p-3 text-xs space-y-1">
        <div className="flex justify-between">
          <span className="text-gray-500">Name</span>
          <span className="font-medium text-gray-800">{informant.firstName} {informant.lastName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Email</span>
          <span className="font-medium text-gray-800 break-all">{informant.email}</span>
        </div>
        {informant.relationship && (
          <div className="flex justify-between">
            <span className="text-gray-500">Relationship</span>
            <span className="font-medium text-gray-800">{informant.relationship}</span>
          </div>
        )}
        {!isAutismPathway && informant.lifeStages?.length > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-500">Assigned to</span>
            <span className="font-medium text-gray-800">{informant.lifeStages.join(', ')}</span>
          </div>
        )}
      </div>

      {/* Optional reason for audit trail */}
      <div>
        <label htmlFor="remove-reason" className="block text-xs font-medium text-gray-700 mb-1">
          Reason for removal <span className="text-gray-400">(optional)</span>
        </label>
        <input
          id="remove-reason"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Added in error, no childhood informant available"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none"
          disabled={actionLoading}
        />
      </div>

      {actionError && <WarningPanel variant="error">{actionError}</WarningPanel>}

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={() => { clearActionState?.(); onBack(); }}
          disabled={actionLoading}
          className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={actionLoading}
          className={`flex-1 px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors ${
            actionLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
          }`}
        >
          {actionLoading ? (
            <span className="inline-flex items-center gap-2">
              <ButtonSpinner />
              Removing…
            </span>
          ) : `Confirm removal`}
        </button>
      </div>
    </div>
  );
}
